import { Button } from '@coop/components';
import { Bag1Icon } from '@coop/icons';

import cartRecipeSelectors from '../../../../cart/selectors/cartRecipeSelectors';
import { useAsyncDispatcher } from '../../../hooks/useAsyncDispatcher';
import { useAppDispatch, useAppSelector } from '../../../hooks/useThunkDispatch';
import { cartThunks } from '../../../thunks/cartThunks';
import { useRecipeTracking } from './BuyableRecipeButton.hooks';

interface BuyableRecipeButtonProps {
    buyButtonRef?: React.RefObject<HTMLButtonElement | null>;
    recipeId: string;
    recipeName: string;
    onBuyClick?: (e: React.SyntheticEvent) => void;
    onRemoveClick?: (e: React.SyntheticEvent) => void;
    buyText?: string;
    removeText?: string;
    buttonSize: 'default' | 'large';
    buttonFullWidth: boolean;
}

const BuyableRecipeButton = ({
    buyButtonRef,
    buttonSize,
    recipeId,
    recipeName,
    onBuyClick,
    onRemoveClick,
    buyText,
    removeText,
    buttonFullWidth,
}: React.PropsWithChildren<BuyableRecipeButtonProps>) => {
    const cartRecipe = useAppSelector((state) =>
        cartRecipeSelectors.all.selectById(state, recipeId),
    );

    const dispatch = useAppDispatch();

    const removeRecipeDispatcher = useAsyncDispatcher(() =>
        dispatch(cartThunks.removeRecipe(recipeId)),
    );
    useRecipeTracking(removeRecipeDispatcher, cartRecipe);

    const handleRecipeRemove = (e: React.SyntheticEvent) => {
        removeRecipeDispatcher.execute();
        onRemoveClick?.(e);
    };

    const onRecipeBuyClick = (e: React.SyntheticEvent) => {
        onBuyClick?.(e);
    };

    const hasRecipeInCart = !!cartRecipe;

    const { isLoading } = removeRecipeDispatcher;

    const buttonRemoveText = removeText || 'Ta bort varor';
    const buttonBuyText = buyText || 'Se & köp';
    const ariaButtonBuyText = buyText || 'Se och köp recept';

    return hasRecipeInCart ? (
        <Button
            size={buttonSize}
            onClick={handleRecipeRemove}
            theme="primary"
            isLoading={isLoading}
            icon={Bag1Icon}
            fullWidth={buttonFullWidth}
            aria-label={`${buttonRemoveText}, ${recipeName}`}
            aria-haspopup={false}
        >
            {buttonRemoveText}
        </Button>
    ) : (
        <Button
            ref={buyButtonRef}
            theme="primary"
            size={buttonSize}
            isLoading={isLoading}
            onClick={onRecipeBuyClick}
            icon={Bag1Icon}
            fullWidth={buttonFullWidth}
            aria-label={`${ariaButtonBuyText}, ${recipeName}`}
            aria-haspopup="dialog"
        >
            {buttonBuyText}
        </Button>
    );
};

export default BuyableRecipeButton;
